import * as Cesium from 'cesium';

class SmartZoomController {
            constructor(viewer) {
                this.viewer = viewer;
                this.scene = viewer.scene;
                this.camera = viewer.camera;
                
                // Configuration
                this.config = {
                    minDistance: 10,          // Minimum distance from target
                    maxDistance: 1000000,        // Maximum distance from target
                    nearZoomFactor: 0.25,      // Zoom factor when close (25% of distance)
                    farZoomFactor: 0.5,        // Zoom factor when far (50% of distance)
                    zoomOutMultiplier: 10,    // Multiplier for zoom out (makes zoom out much stronger)
                    transitionDistance: 500,   // Distance where zoom factor transitions
                    animationDuration: 1.,    // Animation duration in seconds (reduced for snappier feel)
                    collisionPadding: 50       // Padding to avoid collision (increased for visibility)
                };
                
                // Target point (center of point cloud or focus point)
                this.targetPoint = null;
                this.boundingSphere = null;
            }
            
            // Set the target point and bounding sphere for the point cloud
            setTarget(position, boundingRadius = 100) {
                this.targetPoint = position;
                this.boundingSphere = new Cesium.BoundingSphere(position, boundingRadius);
            }
            
            // Calculate adaptive zoom factor based on distance
            calculateZoomFactor(distance) {
                const { nearZoomFactor, farZoomFactor, transitionDistance } = this.config;
                
                // Smooth transition between near and far zoom factors
                if (distance < transitionDistance) {
                    // Linear interpolation for close distances
                    const t = distance / transitionDistance;
                    return nearZoomFactor + (farZoomFactor - nearZoomFactor) * t;
                } else {
                    // Logarithmic scaling for far distances (increased for bigger steps)
                    const logScale = Math.log10(distance / transitionDistance) / 2;
                    return Math.min(farZoomFactor * (1 + logScale), 0.8);
                }
            }
            
            // Calculate current distance from camera to target
            getCurrentDistance() {
                if (!this.targetPoint) {
                    // If no target, use the point camera is looking at
                    const ray = this.camera.getPickRay(new Cesium.Cartesian2(
                        this.scene.canvas.width / 2,
                        this.scene.canvas.height / 2
                    ));
                    const intersection = this.scene.globe.pick(ray, this.scene);
                    if (intersection) {
                        this.targetPoint = intersection;
                    } else {
                        return 1000; // Default distance
                    }
                }
                
                return Cesium.Cartesian3.distance(this.camera.position, this.targetPoint);
            }
            
            // Calculate safe camera position
            calculateSafePosition(targetPosition, direction, requestedDistance) {
                const safePosition = new Cesium.Cartesian3();
                
                // Calculate requested position (direction is from target to camera)
                Cesium.Cartesian3.multiplyByScalar(direction, requestedDistance, safePosition);
                Cesium.Cartesian3.add(targetPosition, safePosition, safePosition);
                
                // Check if we're too close (inside bounding sphere + padding)
                if (this.boundingSphere) {
                    const distanceToCenter = Cesium.Cartesian3.distance(safePosition, this.boundingSphere.center);
                    const minAllowedDistance = this.boundingSphere.radius + this.config.collisionPadding;
                    
                    if (distanceToCenter < minAllowedDistance) {
                        // Push the camera out to the minimum safe distance
                        Cesium.Cartesian3.multiplyByScalar(direction, minAllowedDistance, safePosition);
                        Cesium.Cartesian3.add(targetPosition, safePosition, safePosition);
                        
                        console.log('Collision avoided! Adjusted to minimum distance:', minAllowedDistance);
                    }
                }
                
                return safePosition;
            }
            
            // Perform zoom operation
            zoom(zoomIn) {
                const currentDistance = this.getCurrentDistance();
                let zoomFactor = this.calculateZoomFactor(currentDistance);
                
                // Apply multiplier for zoom out to make it stronger
                if (!zoomIn) {
                    zoomFactor = zoomFactor * this.config.zoomOutMultiplier;
                    // Cap at 90% to prevent jumping too far
                    zoomFactor = Math.min(zoomFactor, 2);
                }
                
                // Calculate new distance
                let newDistance;
                if (zoomIn) {
                    newDistance = Math.max(
                        currentDistance * (1 - zoomFactor),
                        this.config.minDistance
                    );
                } else {
                    newDistance = Math.min(
                        currentDistance * (1 + zoomFactor),
                        this.config.maxDistance
                    );
                }
                
                // Get camera direction (from target to camera for proper orientation)
                const direction = new Cesium.Cartesian3();
                Cesium.Cartesian3.subtract(this.camera.position, this.targetPoint, direction);
                Cesium.Cartesian3.normalize(direction, direction);
                
                // Calculate new position with collision check
                const newPosition = this.calculateSafePosition(this.targetPoint, direction, newDistance);
                
                // Calculate actual distance after collision adjustment
                const actualDistance = Cesium.Cartesian3.distance(newPosition, this.targetPoint);
                
                // Animate camera to new position
                this.animateCamera(newPosition);
                
                // Update info display with actual distance
                this.updateInfo(actualDistance, zoomFactor);
            }
            
            // Smooth camera animation
            animateCamera(newPosition) {
                const startPosition = Cesium.Cartesian3.clone(this.camera.position);
                const startTime = Date.now();
                const duration = this.config.animationDuration * 1000;
                
                const animate = () => {
                    const elapsed = Date.now() - startTime;
                    const t = Math.min(elapsed / duration, 1);
                    
                    // Ease-in-out interpolation
                    const eased = t < 0.5 
                        ? 2 * t * t 
                        : -1 + (4 - 2 * t) * t;
                    
                    // Interpolate position
                    const currentPos = new Cesium.Cartesian3();
                    Cesium.Cartesian3.lerp(startPosition, newPosition, eased, currentPos);
                    
                    // Update camera
                    this.camera.setView({
                        destination: currentPos,
                        orientation: {
                            direction: this.camera.direction,
                            up: this.camera.up
                        }
                    });
                    
                    if (t < 1) {
                        requestAnimationFrame(animate);
                    }
                };
                
                animate();
            }
            
            // Update info display
            updateInfo(distance, zoomFactor) {
                //console.log(distance.toFixed(1) + ' m');
                //console.log((zoomFactor * 100).toFixed(1) + '%');
            }
        }


export default SmartZoomController;